import {
  AppBar,
  Avatar,
  Box,
  CircularProgress,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Toolbar,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  DashboardRounded,
  MenuBookRounded,
  CalendarMonthRounded,
  PaidRounded,
  WorkspacePremiumRounded,
  CampaignRounded,
  EventAvailableRounded,
  MenuRounded,
  LogoutRounded,
  AutoStoriesRounded,
  GroupsRounded,
  SettingsRounded,
  EditNoteRounded,
  FavoriteRounded,
  HistoryRounded,
  SchoolRounded,
  PollRounded,
  ForumRounded,
} from '@mui/icons-material';
import { Navigate, Outlet, useLocation, useNavigate, NavLink } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { signOutAndReload } from '../../lib/session';
import { useAuth } from '../../contexts/auth.context';
import { useTenantStore } from '../../stores/tenant.store';
import { useStudentIdentity } from '../../hooks/use-student-identity';
import { useTenantConfig } from '../../hooks/use-tenant-config';
import { NotificationsBell } from '../../components/notifications/NotificationsBell';
import { usePortalView, usePortalViewStore } from '../../stores/portal-view.store';
import { isPortalViewExpired, isPortalViewPath } from '../../lib/portal-view';
import { PortalViewBanner, PORTAL_VIEW_BANNER_HEIGHT } from './components/PortalViewBanner';

const DRAWER_WIDTH = 240;

interface NavItem {
  labelKey: string;
  path: string;
  icon: React.ReactNode;
}

const NAV_ITEMS: NavItem[] = [
  { labelKey: 'portal.nav.inicio', path: '/portal-v2', icon: <DashboardRounded /> },
  { labelKey: 'portal.nav.enroll', path: '/portal-v2/enroll', icon: <EventAvailableRounded /> },
  { labelKey: 'portal.nav.courses', path: '/portal-v2/courses', icon: <MenuBookRounded /> },
  { labelKey: 'portal.nav.surveys', path: '/portal-v2/surveys', icon: <PollRounded /> },
  { labelKey: 'portal.nav.career', path: '/portal-v2/career', icon: <SchoolRounded /> },
  { labelKey: 'portal.nav.historial', path: '/portal-v2/historial', icon: <HistoryRounded /> },
  { labelKey: 'portal.nav.calendar', path: '/portal-v2/calendar', icon: <CalendarMonthRounded /> },
  { labelKey: 'portal.nav.scripture', path: '/portal-v2/scripture', icon: <AutoStoriesRounded /> },
  { labelKey: 'portal.nav.journal', path: '/portal-v2/journal', icon: <EditNoteRounded /> },
  { labelKey: 'portal.nav.prayer', path: '/portal-v2/prayer', icon: <FavoriteRounded /> },
  { labelKey: 'portal.nav.mentor', path: '/portal-v2/mentor', icon: <GroupsRounded /> },
  {
    labelKey: 'portal.nav.practice',
    path: '/portal-v2/practica-socratica',
    icon: <ForumRounded />,
  },
  {
    labelKey: 'portal.nav.announcements',
    path: '/portal-v2/announcements',
    icon: <CampaignRounded />,
  },
  { labelKey: 'portal.nav.payments', path: '/portal-v2/payments', icon: <PaidRounded /> },
  {
    labelKey: 'portal.nav.scholarships',
    path: '/portal-v2/scholarships',
    icon: <WorkspacePremiumRounded />,
  },
];

export function PortalShell(): JSX.Element {
  const { t } = useTranslation('sis');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [drawerOpen, setDrawerOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { availableTenants, currentTenantId } = useTenantStore();
  // «Ver portal» (lib/portal-view): el personal mira, en solo lectura, el
  // portal de un alumno. Solo las páginas de la versión 1; sin campana, sin
  // cerrar sesión y sin acciones.
  const view = usePortalView();
  const gone = usePortalViewStore((s) => s.gone);
  const markGone = usePortalViewStore((s) => s.markGone);
  const viewing = !!view;
  const queryClient = useQueryClient();
  useEffect(() => {
    if (!view) return undefined;
    const check = () => {
      if (isPortalViewExpired(view)) markGone('expired');
    };
    check();
    const timer = window.setInterval(check, 30_000);
    return () => window.clearInterval(timer);
  }, [view, markGone]);

  const tenantName =
    availableTenants.find((tenant) => tenant.id === currentTenantId)?.name ??
    t('portal.shell.defaultTenantName');
  // Arriba a la derecha: el nombre real del alumno (su ficha), no el de la
  // cuenta de Google ni el correo; en «Ver portal», su código.
  const identity = useStudentIdentity();
  const personName = [identity.firstName, identity.lastName].filter(Boolean).join(' ');
  const displayName = view
    ? (view.studentCode ?? t('portal.view.thisStudent'))
    : personName || user?.displayName || user?.email || t('portal.shell.defaultStudent');
  // Los módulos que el seminario no usa no aparecen (modules.config); y hasta
  // saberlo, el menú espera en vez de mostrar y después esconder.
  const tenantConfig = useTenantConfig();
  const navItems = !tenantConfig.settled
    ? []
    : (viewing ? NAV_ITEMS.filter((i) => isPortalViewPath(i.path)) : NAV_ITEMS).filter((i) =>
        tenantConfig.pathEnabled(i.path),
      );
  const bannerOffset = viewing ? PORTAL_VIEW_BANNER_HEIGHT : 0;
  const initials = displayName
    .split(' ')
    .map((n: string) => n[0] ?? '')
    .join('')
    .slice(0, 2)
    .toUpperCase();

  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: '#0d1117' }}>
      <Box sx={{ px: 3, py: 2.5, borderBottom: '1px solid #21262d' }}>
        <Typography variant="caption" color="#6e7681" fontWeight={700} sx={{ letterSpacing: 0.5 }}>
          {t('portal.shell.seminaryLabel')}
        </Typography>
        <Typography variant="body2" fontWeight={700} color="#e6edf3" noWrap>
          {tenantName}
        </Typography>
      </Box>

      <List sx={{ flex: 1, px: 1.5, py: 1.5 }}>
        {navItems.map((item) => {
          const isActive =
            item.path === '/portal-v2'
              ? location.pathname === '/portal-v2'
              : location.pathname.startsWith(item.path);
          return (
            <ListItemButton
              key={item.path}
              component={NavLink}
              to={item.path}
              onClick={() => isMobile && setDrawerOpen(false)}
              sx={{
                borderRadius: 1.5,
                mb: 0.5,
                color: isActive ? '#fff' : '#8b949e',
                bgcolor: isActive ? 'rgba(99,102,241,0.18)' : 'transparent',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.04)' },
              }}
            >
              <ListItemIcon sx={{ color: 'inherit', minWidth: 36 }}>{item.icon}</ListItemIcon>
              <ListItemText
                primary={t(item.labelKey)}
                primaryTypographyProps={{ fontSize: 14, fontWeight: isActive ? 600 : 400 }}
              />
            </ListItemButton>
          );
        })}
      </List>

      <Box sx={{ borderTop: '1px solid #21262d', p: 1.5, display: viewing ? 'none' : 'block' }}>
        <ListItemButton
          component={NavLink}
          to="/portal-v2/settings/notifications"
          onClick={() => isMobile && setDrawerOpen(false)}
          sx={{ borderRadius: 1.5, color: '#8b949e' }}
        >
          <ListItemIcon sx={{ color: 'inherit', minWidth: 36 }}>
            <SettingsRounded />
          </ListItemIcon>
          <ListItemText
            primary={t('portal.nav.preferences')}
            primaryTypographyProps={{ fontSize: 13 }}
          />
        </ListItemButton>
      </Box>
    </Box>
  );

  return (
    <Box
      sx={{
        display: 'flex',
        minHeight: '100vh',
        bgcolor: '#0d1117',
        color: '#e6edf3',
        overflowX: 'hidden',
      }}
    >
      {view && (
        <PortalViewBanner
          view={view}
          onExit={() => {
            queryClient.removeQueries();
            usePortalViewStore.getState().exit();
            navigate(`/people/${view.personId}`, { replace: true });
          }}
        />
      )}

      {/* Mobile AppBar */}
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          display: { md: 'none' },
          bgcolor: '#0d1117',
          borderBottom: '1px solid #21262d',
          top: bannerOffset,
        }}
      >
        <Toolbar>
          <IconButton edge="start" onClick={() => setDrawerOpen(true)} sx={{ color: '#fff' }}>
            <MenuRounded />
          </IconButton>
          <Typography variant="subtitle1" fontWeight={700} sx={{ flex: 1 }} noWrap>
            {tenantName}
          </Typography>
          {!viewing && <NotificationsBell />}
          <Avatar sx={{ ml: 1, width: 32, height: 32, bgcolor: 'primary.main', fontSize: 13 }}>
            {initials}
          </Avatar>
        </Toolbar>
      </AppBar>

      {/* Drawer */}
      <Box component="nav" sx={{ width: { md: DRAWER_WIDTH }, flexShrink: 0 }}>
        <Drawer
          variant="temporary"
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { width: DRAWER_WIDTH, borderRight: '1px solid #21262d' },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          open
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': {
              width: DRAWER_WIDTH,
              borderRight: '1px solid #21262d',
              top: bannerOffset,
              height: `calc(100% - ${bannerOffset}px)`,
            },
          }}
        >
          {drawer}
        </Drawer>
      </Box>

      {/* Main content */}
      <Box
        component="main"
        sx={{
          flex: 1,
          minWidth: 0,
          minHeight: '100vh',
          mt: { xs: `${56 + bannerOffset}px`, md: `${bannerOffset}px` },
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Desktop topbar */}
        <Box
          sx={{
            display: { xs: 'none', md: 'flex' },
            alignItems: 'center',
            justifyContent: 'flex-end',
            px: 3,
            py: 1.5,
            borderBottom: '1px solid #21262d',
            gap: 1,
          }}
        >
          {!viewing && <NotificationsBell />}
          <Tooltip title={displayName}>
            <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main', fontSize: 13 }}>
              {initials}
            </Avatar>
          </Tooltip>
          <Typography variant="body2" color="#c9d1d9" sx={{ ml: 1 }}>
            {displayName}
          </Typography>
          {!viewing && (
            <Tooltip title={t('portal.shell.signOut')}>
              <IconButton
                onClick={() => void signOutAndReload()}
                sx={{ color: '#8b949e' }}
                size="small"
              >
                <LogoutRounded fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>

        {/* Page content — en la vista, solo las páginas de la versión 1 */}
        <Box sx={{ flex: 1, minWidth: 0, p: { xs: 2, md: 4 } }}>
          {viewing && gone ? (
            <PortalEmpty
              icon={<HistoryRounded fontSize="inherit" />}
              title={
                gone === 'deceased'
                  ? t('portal.view.deceasedTitle')
                  : gone === 'invalid'
                    ? t('portal.view.invalidTitle')
                    : t('portal.view.expiredTitle')
              }
              body={
                gone === 'deceased'
                  ? t('portal.view.deceasedBody')
                  : gone === 'invalid'
                    ? t('portal.view.invalidBody')
                    : t('portal.view.expiredBody')
              }
            />
          ) : viewing && !isPortalViewPath(location.pathname) ? (
            <Navigate to="/portal-v2" replace />
          ) : (
            <Outlet />
          )}
        </Box>
      </Box>
    </Box>
  );
}

export function PortalLoading(): JSX.Element {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 200 }}>
      <CircularProgress />
    </Box>
  );
}

export function PortalEmpty({
  icon,
  title,
  body,
}: {
  icon?: React.ReactNode;
  title: string;
  body?: string;
}): JSX.Element {
  return (
    <Stack
      alignItems="center"
      justifyContent="center"
      sx={{ py: 8, textAlign: 'center' }}
      spacing={1.5}
    >
      {icon && <Box sx={{ color: '#6e7681', fontSize: 48 }}>{icon}</Box>}
      <Typography variant="h6" color="#c9d1d9">
        {title}
      </Typography>
      {body && (
        <Typography variant="body2" color="#8b949e" sx={{ maxWidth: 460 }}>
          {body}
        </Typography>
      )}
    </Stack>
  );
}
